import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    Output,
    ViewChild,
    ViewEncapsulation,
} from "@angular/core"
import { Subscription } from "rxjs"
import { MuxDataModel } from "../../models/muxData.model"
import { AudioMedia } from "../../models/resource/media"
import { ResourceService } from "../../services/resource.service"
import { ScriptService } from "../../services/script-service.service"
import { UserAgentService } from "../../services/user-agent.service"
import { UserService } from "../../services/user.service"
import PlayerUtils from "../../utils/player-utils"
import { GtmVideoJsInfo } from "../../utils/types"
import { GTMService } from "../../services/gtm.service"

declare let window: any
declare const videojs: any

/*
Based on https://docs.videojs.com/tutorial-angular.html.
Duplicated in the most part for video.component.ts. Please consider this when making changes.
 */
@Component({
    selector: "app-audio",
    templateUrl: "./audio.component.html",
    styleUrls: ["./audio.component.scss"],
    // DOM elements added by video.js do not have Angular namespaces and break, so disable this
    encapsulation: ViewEncapsulation.None,
})
export class AudioComponent implements AfterViewInit, OnChanges {
    @Input() public audioMedia: AudioMedia
    @Input() public muxData: MuxDataModel = new MuxDataModel()
    @Input() public gtmVideoJsInfo: GtmVideoJsInfo
    @Output() public audioPlayerReady: EventEmitter<any> = new EventEmitter<any>()
    public audioPlayer: any = null
    public subscriptions: Subscription[] = []
    @ViewChild("player") public target: ElementRef
    private viewInit = false
    private lastInitializedMedia: AudioMedia
    private firstInitialize: boolean = false

    constructor(
        public userAgentService: UserAgentService,
        private resourceService: ResourceService,
        private zone: NgZone,
        private userService: UserService,
        private scriptService: ScriptService,
        private gtmService: GTMService
    ) {
        this.viewInit = false
    }

    public ngAfterViewInit() {
        this.viewInit = true
        this.initAudio()
    }

    public ngOnChanges() {
        if (this.audioMedia !== this.lastInitializedMedia) {
            this.expectGeoRestrictionEvaluated()
            this.initAudio()
        }
    }

    public ngOnDestroy() {
        if (this.audioPlayer) {
            // https://docs.videojs.com/tutorial-player-workflows.html#dispose
            // When the component is destroyed we still want to clean the DOM and the memory
            this.audioPlayer.dispose()
        }
        this.subscriptions.forEach((subscription) => subscription.unsubscribe())
    }

    private expectGeoRestrictionEvaluated() {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe())
        this.subscriptions = [
            this.audioMedia.geoRestrictionEvaluated.subscribe((evaluatedMedia) => {
                if (evaluatedMedia !== this.audioMedia) {
                    return
                }
                this.initAudio()
            }),
        ]
    }

    private initAudio() {
        if (!this.audioMedia || !this.viewInit || !this.audioMedia.isGeoRestrictionEvaluated()) {
            // If media is not ready, it cannot be initialized
            // If element is not rendered, it cannot be initialized
            return
        }

        this.lastInitializedMedia = this.audioMedia
        // Everything is ready for player specific initialization

        this.scriptService
            .load("videojs", "wavesurfer", "videojs.css", "videojs-seek-chapter.css")
            .then(() => {
                const audioPlugins = [
                    "videojs-mux",
                    "videojs-seek-buttons",
                    "videojs-errors",
                    "videojs-hotkeys",
                    "videojs-wavesurfer",
                    "videojs-seek-chapter",
                ]
                // Plugins must be loaded after video.js is loaded in order to be registered.
                this.scriptService.load(...audioPlugins).then(() => {
                    this.initVideoJS()
                })
            })
    }

    private initVideoJS() {
        /*
        We need to account that the component will be kept and the AudioMedia will change (gallery example).
        So instantiate the player only once and assume it needs cleaning.
        https://docs.videojs.com/player#reset preferred, but this would also work:
        this.audioPlayer.remoteTextTracks().tracks_.forEach((track) => {
            this.audioPlayer.removeRemoteTextTrack(track);
        });
         */
        if (!this.audioPlayer) {
            const hotKeysSettings = {
                seekStep: 10,
                enableVolumeScroll: false,
            }
            // First instantiation. Not setting options here because it would prevent it from being reset
            this.audioPlayer = videojs(this.target.nativeElement, {
                playbackRates: [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2],
                controlBar: {
                    volumePanel: { inline: false },
                },
                fluid: true,
                html5: {
                    nativeTextTracks: false,
                },
                plugins: {
                    seekChapter: {
                        chapterDisplayLength: 3,
                    },
                    wavesurfer: {
                        backend: "MediaElement",
                        displayMilliseconds: false,
                        hideScrollbar: true,
                        waveColor: "white",
                        progressColor: "#48D3CD",
                        // Since progress is not aligned, use the same color as the background to make it less obvious
                        cursorColor: "#0a145a",
                    },
                },
            })
            if (this.audioPlayer.seekButtons) {
                this.audioPlayer.seekButtons({ back: 10 })
            }
            if (this.audioPlayer.errors) {
                this.audioPlayer.errors({
                    dismiss: false,
                    errors: PlayerUtils.ErrorOverrides,
                })
            }
            this.audioPlayer.controlBar.removeChild("pictureInPictureToggle")
            this.audioPlayer.controlBar.removeChild("fullscreenToggle")
            PlayerUtils.finalizePlayerSetup(
                this.audioPlayer,
                this.gtmService,
                this.gtmVideoJsInfo,
                hotKeysSettings
            )
            this.firstInitialize = true
        } else {
            // moved to a different AudioMedia
            this.firstInitialize = false
            this.audioPlayer.pause() // Without this, the reset is causing a loading state
            // When moving from audio to audio the play button gets stuck, so explicitly pause it
            this.audioPlayer.controlBar.playToggle.handlePause()
            this.audioPlayer.reset()
        }
        this.updateVideoJS()
    }

    private updateVideoJS() {
        if (this.audioMedia.isAvailableToUser()) {
            this.audioPlayer.src(this.audioMedia.getVideoJsSource())
        } else {
            this.audioPlayer.error({ code: "PLAYER_ERR_GEO_RESTRICTED" })
        }
        this.muxData.setupForVideoJS(this.audioPlayer, this.firstInitialize)
        PlayerUtils.loadCaptionLanguage(this.audioPlayer, this.audioMedia)
        PlayerUtils.trackCaptionSettingsState(this.audioPlayer)
        this.gtmService.setMediaType(this.gtmVideoJsInfo.type)
        this.gtmService.setVideoInfo(`${this.gtmVideoJsInfo.title} | ${this.gtmVideoJsInfo.guid}`)
    }
}
